import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientForm from './ClientForm';
import Modal from './Modal';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSave = async (clientData) => {
    try {
      if (editingClient) {
        await axios.put(`${API_BASE}/clients/${editingClient.id}`, clientData);
      } else {
        await axios.post(`${API_BASE}/clients`, clientData);
      }
      await fetchClients();
      setShowClientForm(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error saving client: ${errorMessage}`);
    }
  };

  const handleClientDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${API_BASE}/clients/${id}`);
        await fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Error deleting client. Please try again.');
      }
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowClientForm(true);
  };

  // Filter clients based on search query
  const filteredClients = clients.filter(client => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (client.name && client.name.toLowerCase().includes(query)) ||
      (client.company && client.company.toLowerCase().includes(query)) ||
      (client.email && client.email.toLowerCase().includes(query)) ||
      (client.phone && client.phone.toLowerCase().includes(query)) ||
      (client.vat_id && client.vat_id.toLowerCase().includes(query)) ||
      (client.notes && client.notes.toLowerCase().includes(query))
    );
  });

  return (
    <div className="clients-page">
      <div className="clients-header">
        <div>
          <h1>Clients</h1>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingClient(null);
            setShowClientForm(true);
          }}
        >
          + New Client
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="Search clients by name, company, email, phone, VAT ID, or notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <Modal
        isOpen={showClientForm}
        onClose={() => {
          setShowClientForm(false);
          setEditingClient(null);
        }}
        title={editingClient ? 'Edit Client' : 'New Client'}
      >
        <ClientForm
          client={editingClient}
          onSave={(clientData) => {
            handleClientSave(clientData);
            setShowClientForm(false);
            setEditingClient(null);
          }}
          onCancel={() => {
            setShowClientForm(false);
            setEditingClient(null);
          }}
        />
      </Modal>

      <div className="clients-list">
        {filteredClients.length > 0 ? (
          <div className="clients-grid">
            {filteredClients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-card-header">
                  <h3>{client.name}</h3>
                  <div className="client-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEditClient(client)}
                      title="Edit client"
                    >
                      Edit
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleClientDelete(client.id)}
                      title="Delete client"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="client-card-body">
                  {client.company && (
                    <div className="client-info">
                      <strong>Company:</strong> {client.company}
                    </div>
                  )}
                  {client.email && (
                    <div className="client-info">
                      <strong>Email:</strong> <a href={`mailto:${client.email}`}>{client.email}</a>
                    </div>
                  )}
                  {client.phone && (
                    <div className="client-info">
                      <strong>Phone:</strong> <a href={`tel:${client.phone}`}>{client.phone}</a>
                    </div>
                  )}
                  {client.vat_id && (
                    <div className="client-info">
                      <strong>VAT ID:</strong> {client.vat_id}
                    </div>
                  )}
                  {client.notes && (
                    <div className="client-info">
                      <strong>Notes:</strong> {client.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : clients.length > 0 ? (
          <div className="empty-state">
            <p>No clients match your search query.</p>
          </div>
        ) : (
          <div className="empty-state">
            <p>No clients yet. Click "+ New Client" to add your first client.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientsPage;

